// The render-only keys EmailHtmlService.getHtmlForTemplate injects on top of the
// per-template context — valid in body partials but not in subject/header. The service
// types its injected context as EmailBodyInjectedVariables so this list can't drift.
export const EMAIL_BODY_INJECTED_VARIABLE_NAMES = [
  'title',
  'headerMessage',
  'logoUrl',
  'primaryColor',
  'greeting',
] as const;

export type EmailBodyInjectedVariables = Record<(typeof EMAIL_BODY_INJECTED_VARIABLE_NAMES)[number], string>;

export interface TemplateVariableReference {
  name: string;
  start: number;
  end: number;
}

// The only JavaScript email templates may use: conditionals, for...of loops, comparisons,
// const/let declarations, and exploit-free built-ins. `function` is included because
// default templates use forEach callbacks (and a token scan can't detect arrow functions
// anyway). Code-execution surface (Reflect, eval, Function, process, …) is deliberately
// not supported.
const SUPPORTED_JS_NAMES = new Set([
  'if',
  'else',
  'switch',
  'case',
  'default',
  'break',
  'for',
  'of',
  'typeof',
  'instanceof',
  'null',
  'undefined',
  'true',
  'false',
  'const',
  'let',
  'function',
  'new',
  'Array',
  'Object',
  'String',
  'Date',
  'Math',
  'JSON',
  'Map',
  'Set',
  'parseInt',
  'parseFloat',
  'encodeURIComponent',
  'decodeURIComponent',
  'encodeURI',
  'decodeURI',
  // EJS's render-context accessor; interestRateAdjustment uses locals.fixedPeriodLabel to
  // presence-check that optional variable.
  'locals',
]);

// Property access is otherwise unrestricted (any data field must pass), but these three are
// the standard route from template data to arbitrary code.
const FORBIDDEN_MEMBER_NAMES = new Set(['constructor', 'prototype', '__proto__']);

// Computed access (x['constructor']) would smuggle the forbidden names past the identifier
// scan, whose string-blanking erases the literal. Runs against the raw island body.
const FORBIDDEN_COMPUTED_MEMBER_PATTERN = new RegExp(
  `\\[\\s*(['"\`])(${[...FORBIDDEN_MEMBER_NAMES].join('|')})\\1\\s*\\]`,
  'g',
);

interface EjsIsland {
  body: string;
  bodyStart: number;
}

function extractEjsIslands(template: string): EjsIsland[] {
  const islands: EjsIsland[] = [];
  const pattern = /<%([\s\S]*?)%>/g;
  let match = pattern.exec(template);
  while (match) {
    let body = match[1];
    let bodyStart = match.index + 2;
    const flag = body[0];
    // `<%%` is EJS's escape for a literal `<%`; `<%#` is a comment — neither executes.
    if (flag !== '%' && flag !== '#') {
      // One-character tag flags, not code: `=` escaped output, `-` raw output, `_`
      // whitespace-slurping scriptlet. Strip so offsets point at the JavaScript.
      if (flag === '=' || flag === '-' || flag === '_') {
        body = body.slice(1);
        bodyStart += 1;
      }
      // `-%>` / `_%>` are whitespace-control closers; the trailing char isn't code either.
      if (body.endsWith('-') || body.endsWith('_')) {
        body = body.slice(0, -1);
      }
      islands.push({ body, bodyStart });
    }
    match = pattern.exec(template);
  }
  return islands;
}

// Replaces string-literal and comment characters with spaces so the identifier scan can't
// match words inside them, while every surviving character keeps its original offset.
// Template-literal static text is blanked too, but `${ … }` interpolations are real code
// and stay visible to the scan.
function blankStringsAndComments(body: string): string {
  const chars = body.split('');
  // The top frame is either template-literal text or code; code frames track brace depth so
  // a `}` closing an object literal isn't mistaken for the end of a `${ … }` interpolation.
  const stack: { template: boolean; braceDepth: number }[] = [{ template: false, braceDepth: 0 }];
  let i = 0;
  while (i < chars.length) {
    const frame = stack[stack.length - 1];
    const char = chars[i];

    if (frame.template) {
      if (char === '\\') {
        chars[i] = ' ';
        if (i + 1 < chars.length) chars[i + 1] = ' ';
        i += 2;
      } else if (char === '`') {
        stack.pop();
        i += 1;
      } else if (char === '$' && chars[i + 1] === '{') {
        // Blank the `$` so it can't match as an identifier; the interpolation code after it
        // is scanned like any other code.
        chars[i] = ' ';
        stack.push({ template: false, braceDepth: 0 });
        i += 2;
      } else {
        chars[i] = ' ';
        i += 1;
      }
    } else if (char === "'" || char === '"') {
      i += 1;
      while (i < chars.length && chars[i] !== char) {
        if (chars[i] === '\\') {
          chars[i] = ' ';
          i += 1;
        }
        if (i < chars.length) chars[i] = ' ';
        i += 1;
      }
      i += 1;
    } else if (char === '`') {
      stack.push({ template: true, braceDepth: 0 });
      i += 1;
    } else if (char === '/' && chars[i + 1] === '/') {
      while (i < chars.length && chars[i] !== '\n') {
        chars[i] = ' ';
        i += 1;
      }
    } else if (char === '/' && chars[i + 1] === '*') {
      while (i < chars.length && !(chars[i] === '*' && chars[i + 1] === '/')) {
        chars[i] = ' ';
        i += 1;
      }
      if (i < chars.length) {
        chars[i] = ' ';
        chars[i + 1] = ' ';
        i += 2;
      }
    } else if (char === '{') {
      frame.braceDepth += 1;
      i += 1;
    } else if (char === '}') {
      if (frame.braceDepth === 0 && stack.length > 1) {
        stack.pop();
      } else {
        frame.braceDepth = Math.max(0, frame.braceDepth - 1);
      }
      i += 1;
    } else {
      i += 1;
    }
  }
  return chars.join('');
}

const IDENTIFIER_PATTERN = /[A-Za-z_$][A-Za-z0-9_$]*/g;

function collectDeclaredNames(blankedBodies: string[]): Set<string> {
  const declared = new Set<string>();
  const addIdentifiers = (fragment: string | undefined) => {
    if (!fragment) return;
    for (const name of fragment.match(IDENTIFIER_PATTERN) ?? []) {
      declared.add(name);
    }
  };

  for (const body of blankedBodies) {
    for (const match of body.matchAll(/\b(?:const|let|var)\s+([A-Za-z_$][A-Za-z0-9_$]*|[{[][^=;]*)/g)) {
      addIdentifiers(match[1]);
    }
    for (const match of body.matchAll(/\bfunction\s*[A-Za-z0-9_$]*\s*\(([^)]*)\)/g)) {
      addIdentifiers(match[1]);
    }
    for (const match of body.matchAll(/\(([^()]*)\)\s*=>/g)) {
      addIdentifiers(match[1]);
    }
    for (const match of body.matchAll(/(?<![A-Za-z0-9_$).])([A-Za-z_$][A-Za-z0-9_$]*)\s*=>/g)) {
      declared.add(match[1]);
    }
    for (const match of body.matchAll(/\bcatch\s*\(\s*([A-Za-z_$][A-Za-z0-9_$]*)/g)) {
      declared.add(match[1]);
    }
  }
  return declared;
}

function previousNonSpaceChar(text: string, index: number): string | undefined {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (!/\s/.test(text[i])) return text[i];
  }
  return undefined;
}

function nextNonSpaceChar(text: string, index: number): string | undefined {
  for (let i = index; i < text.length; i += 1) {
    if (!/\s/.test(text[i])) return text[i];
  }
  return undefined;
}

/**
 * Best-effort scan of an EJS template for references that are not available: variables
 * outside `allowedNames` and JavaScript outside SUPPORTED_JS_NAMES. Skips property access
 * (except the constructor/prototype escapes) and locally-declared names. The same scan
 * backs the editor squiggles and the save-time validation.
 */
export function findUnknownTemplateVariables(
  template: string,
  allowedNames: readonly string[],
): TemplateVariableReference[] {
  const allowed = new Set(allowedNames);
  const islands = extractEjsIslands(template);
  const blankedBodies = islands.map((island) => blankStringsAndComments(island.body));
  const declared = collectDeclaredNames(blankedBodies);

  const unknown: TemplateVariableReference[] = [];
  islands.forEach((island, islandIndex) => {
    for (const match of island.body.matchAll(FORBIDDEN_COMPUTED_MEMBER_PATTERN)) {
      const nameIndex = (match.index ?? 0) + match[0].indexOf(match[2]);
      unknown.push({
        name: match[2],
        start: island.bodyStart + nameIndex,
        end: island.bodyStart + nameIndex + match[2].length,
      });
    }

    const body = blankedBodies[islandIndex];
    for (const match of body.matchAll(IDENTIFIER_PATTERN)) {
      const name = match[0];
      const index = match.index ?? 0;
      const before = previousNonSpaceChar(body, index);

      const isMemberAccess = before === '.';
      if (isMemberAccess && !FORBIDDEN_MEMBER_NAMES.has(name)) continue;
      if (!isMemberAccess) {
        if (allowed.has(name) || declared.has(name) || SUPPORTED_JS_NAMES.has(name)) continue;
        const after = nextNonSpaceChar(body, index + name.length);
        if (after === ':' && (before === '{' || before === ',')) continue;
      }

      unknown.push({
        name,
        start: island.bodyStart + index,
        end: island.bodyStart + index + name.length,
      });
    }
  });
  return unknown;
}
